/**
 * ROI Calculator Constants
 * Investment effectiveness data and templates
 */

import { SecurityInvestment, ThreatType, InvestmentCategory } from '@/types/calculator';

// ============================================================================
// INVESTMENT EFFECTIVENESS DATA
// ============================================================================

export const INVESTMENT_EFFECTIVENESS: Record<string, {
  riskReduction: number;
  threatScope: number;
  source: string;
  primaryThreat: string;
}> = {
  mfa: {
    riskReduction: 99.2,
    threatScope: 0.40,  // Account-based attacks ~40% of breaches
    source: 'Microsoft Digital Defense Report 2024',
    primaryThreat: 'Account compromise'
  },
  edr: {
    riskReduction: 25,
    threatScope: 1.00,  // Broad coverage
    source: 'IBM Cost of Breach Report 2024',
    primaryThreat: 'Malware, ransomware, advanced threats'
  },
  mdr_soc: {
    riskReduction: 45,
    threatScope: 1.00,
    source: 'IBM Cost of Breach Report 2024',
    primaryThreat: 'All threats (faster detection)'
  },
  security_awareness: {
    riskReduction: 70,
    threatScope: 0.35,  // Human-based attacks ~35% of breaches
    source: 'Proofpoint State of the Phish 2024',
    primaryThreat: 'Phishing, social engineering'
  },
  backup_dr: {
    riskReduction: 95,
    threatScope: 0.25,  // Ransomware ~25% of breaches
    source: 'Veeam Ransomware Trends Report 2024',
    primaryThreat: 'Ransomware impact'
  },
  vulnerability_mgmt: {
    riskReduction: 60,
    threatScope: 0.20,  // Exploit-based ~20% of breaches
    source: 'Tenable Cyber Exposure Trends 2024',
    primaryThreat: 'Exploit-based attacks'
  },
  siem: {
    riskReduction: 35,
    threatScope: 1.00,
    source: 'Gartner Market Guide for SIEM 2024',
    primaryThreat: 'Advanced persistent threats'
  },
  email_security: {
    riskReduction: 88,
    threatScope: 0.30,  // Email attacks ~30% of breaches
    source: 'Mimecast State of Email Security 2024',
    primaryThreat: 'Email-based attacks'
  },
  iam_pam: {
    riskReduction: 78,
    threatScope: 0.25,  // Insider/credential abuse ~25%
    source: 'CyberArk Global Advanced Threat Landscape 2024',
    primaryThreat: 'Insider threats, credential abuse'
  },
  network_security: {
    riskReduction: 55,
    threatScope: 1.00,
    source: 'Palo Alto Networks Unit 42 Report 2024',
    primaryThreat: 'Network intrusions'
  }
};

// ============================================================================
// PRE-BUILT SCENARIO TEMPLATES
// ============================================================================

export const SCENARIO_TEMPLATES: Record<string, Partial<SecurityInvestment>> = {
  mdr_soc: {
    name: '24/7 SOC/MDR Service',
    category: 'detection_monitoring',
    implementationCost: 25000,
    annualRecurringCost: 150000,
    personnelHours: 100,
    riskMitigationPercent: 45,
    timeToEffectiveness: 'short',
    confidenceLevel: 'high',
    threatsAddressed: ['ransomware', 'malware', 'data_exfiltration', 'zero_day'],
    complianceBenefits: ['soc2'],
    insurancePremiumReduction: 20
  },
  mfa: {
    name: 'Multi-Factor Authentication (MFA)',
    category: 'identity_access',
    implementationCost: 10000,
    annualRecurringCost: 18000,
    personnelHours: 80,
    riskMitigationPercent: 39.7, // 99.2% × 40% scope
    timeToEffectiveness: 'immediate',
    confidenceLevel: 'high',
    threatsAddressed: ['account_takeover', 'phishing', 'bec'],
    complianceBenefits: ['soc2'],
    insurancePremiumReduction: 10
  },
  edr: {
    name: 'EDR/XDR Platform',
    category: 'prevention_protection',
    implementationCost: 15000,
    annualRecurringCost: 96000,
    personnelHours: 200,
    riskMitigationPercent: 25,
    timeToEffectiveness: 'short',
    confidenceLevel: 'high',
    threatsAddressed: ['ransomware', 'malware', 'zero_day', 'insider_threat'],
    insurancePremiumReduction: 15
  },
  vciso: {
    name: 'Virtual CISO (vCISO)',
    category: 'compliance_governance',
    implementationCost: 5000,
    annualRecurringCost: 120000,
    personnelHours: 50,
    riskMitigationPercent: 30,
    timeToEffectiveness: 'medium',
    confidenceLevel: 'medium',
    threatsAddressed: ['ransomware', 'phishing', 'insider_threat'],
    complianceBenefits: ['soc2', 'hipaa', 'pci_dss'],
    avoidedAuditCosts: 75000
  },
  security_awareness: {
    name: 'Security Awareness Training',
    category: 'training_awareness',
    implementationCost: 8000,
    annualRecurringCost: 30000,
    personnelHours: 120,
    riskMitigationPercent: 24.5, // 70% × 35% scope
    timeToEffectiveness: 'long',
    confidenceLevel: 'medium',
    threatsAddressed: ['phishing', 'bec', 'insider_threat'],
    complianceBenefits: ['hipaa', 'pci_dss'],
    productivitySavings: 15000
  },
  backup_dr: {
    name: 'Backup & Disaster Recovery',
    category: 'backup_recovery',
    implementationCost: 30000,
    annualRecurringCost: 120000,
    personnelHours: 150,
    riskMitigationPercent: 23.75, // 95% × 25% scope
    timeToEffectiveness: 'immediate',
    confidenceLevel: 'high',
    threatsAddressed: ['ransomware'],
    complianceBenefits: ['hipaa', 'soc2'],
    insurancePremiumReduction: 25
  },
  vulnerability_mgmt: {
    name: 'Vulnerability Management',
    category: 'vulnerability_management',
    implementationCost: 20000,
    annualRecurringCost: 72000,
    personnelHours: 250,
    riskMitigationPercent: 12, // 60% × 20% scope
    timeToEffectiveness: 'short',
    confidenceLevel: 'high',
    threatsAddressed: ['unpatched_vulnerabilities', 'zero_day', 'sql_injection'],
    complianceBenefits: ['pci_dss'],
    insurancePremiumReduction: 10
  }
};

// ============================================================================
// PERSONNEL COST
// ============================================================================

export const LOADED_HOURLY_RATE = 75; // Average IT staff loaded cost

// ============================================================================
// DISCOUNT RATE (for NPV calculations)
// ============================================================================

export const DISCOUNT_RATE = 0.08; // 8% WACC estimate

// ============================================================================
// COMPLIANCE VALUE
// ============================================================================

export const COMPLIANCE_VALUES: Record<string, number> = {
  hipaa: 35000,
  pci_dss: 60000,
  soc2: 45000,
  gdpr: 50000,
  cmmc: 80000,
  glba: 30000,
  state_privacy: 25000,
  iso27001: 50000,
  nist_csf: 35000
};

// ============================================================================
// CATEGORY DESCRIPTIONS
// ============================================================================

export const CATEGORY_DESCRIPTIONS: Record<InvestmentCategory, string> = {
  detection_monitoring: 'Security monitoring, threat detection, and incident alerting',
  prevention_protection: 'Preventive security controls and threat blocking',
  identity_access: 'Identity management, access controls, and authentication',
  compliance_governance: 'Compliance frameworks, governance, and risk management',
  backup_recovery: 'Data backup, disaster recovery, and business continuity',
  training_awareness: 'Security awareness training and phishing simulations',
  incident_response: 'Incident response planning and breach response services',
  vulnerability_management: 'Vulnerability scanning, patch management, and remediation',
  infrastructure: 'Security infrastructure, hardware, and network security'
};

// ============================================================================
// THREAT DESCRIPTIONS
// ============================================================================

export const THREAT_DESCRIPTIONS: Record<ThreatType, string> = {
  ransomware: 'Ransomware attacks that encrypt data and demand payment',
  phishing: 'Phishing emails and social engineering attacks',
  malware: 'Malicious software including viruses, trojans, and spyware',
  account_takeover: 'Unauthorized access to user accounts and credentials',
  insider_threat: 'Malicious or negligent actions by employees or contractors',
  ddos: 'Distributed denial of service attacks',
  data_exfiltration: 'Unauthorized data theft and exfiltration',
  sql_injection: 'SQL injection and web application attacks',
  zero_day: 'Zero-day exploits and unknown vulnerabilities',
  supply_chain: 'Supply chain and third-party vendor attacks',
  bec: 'Business email compromise and fraud',
  unpatched_vulnerabilities: 'Exploitation of known, unpatched vulnerabilities'
};
